import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { SectionHeader, Body } from '@grnhse/seedling/lib/birch/typography';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import SidePanel, { SidePanelHandle } from 'shared/components/side_panel';
import LoadingSpinner from '@grnhse/seedling/lib/birch/components/loading_spinner';
import { withLocalePrefix } from 'shared/utils/translation';
import { Document, Row, Person } from 'talent_matching/types';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import { useUserContext } from 'talent_matching/contexts/user_context';
import DocumentHeader from 'talent_matching/components/shared/document_header';
import DocumentContent from 'talent_matching/components/shared/document_content';
import { LinkedinContent, LinkedinHeader } from './linkedin_panel';
import RowStepper from 'talent_matching/components/side_panel/row_stepper';
import {
  useEscapeKeyToClose,
  useExtendedPreviewVsModal,
  useDefaultSidePanelTab,
  usePreventScroll,
  useSyncResumePreviewOnCandidateChange,
  useViewportWidth,
} from './candidate_side_panel_hooks';
import {
  EXTENDED_GUTTER,
  PANEL_BASE_WIDTH,
  EXTENDED_PREVIEW_MAX_WIDTH,
  MIN_EXTENDED_VIEW_WIDTH,
  FullHeightStack,
  PanelContent,
  Footer,
  SidePanelIconButton,
  PanelLayout,
  ResumePreviewContainer,
  ResumePreviewBody,
  BorderedFrame,
  RightPanelColumn,
} from './shared';

const t = withLocalePrefix('talent_matching');

export const CandidateName = styled(SectionHeader)`
  color: ${FullPalette.EVERGREEN_700};
`;

export type SidePanelNavigationProps = {
  onClose: () => void;
  applicationId: number;
  onToggle: () => void;
  isVisible?: boolean;
  rowCount?: number;
  currentIndex?: number | null;
  previousCandidate?: Row | null;
  nextCandidate?: Row | null;
  selectCandidate?: (row: Row) => void;
};

type SidePanelApplication = {
  id: number;
  resume?: { document: Document } | null;
};

export type BaseSidePanelRenderProps<
  T extends SidePanelApplication = SidePanelApplication
> = {
  application: T;
  isLoading: boolean;
  setExtendedResumePreview: (doc: Document | null) => void;
  setLinkedinPreview: (url: string | null) => void;
  setModalDocument: (doc: Document | null) => void;
  extendedResumePreview: Document | null;
  modalDocument: Document | null;
  allowExtendedPreview: boolean;
};

type Props<T extends SidePanelApplication> = SidePanelNavigationProps & {
  // Panel ref wiring — provided by parent via useCandidateSidePanel
  handlePanelRef: (node: SidePanelHandle | null) => void;

  // Candidate data — provided by parent via useCandidateSidePanel
  application: T | null | undefined;
  isLoading: boolean;

  // Tab management
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;

  // Document preview
  canDownloadCandidateData?: boolean;

  // External modal state — OR'd with internal modalDocument check
  externalDisableClickOutside?: boolean;
  // Modals that should block escape-to-close (not including modalDocument)
  externalModalsOpen?: boolean;

  // Render props
  renderHeader: (props: BaseSidePanelRenderProps<T>) => React.ReactNode;
  children: (props: BaseSidePanelRenderProps<T>) => React.ReactNode;
};

const BaseSidePanel = <T extends SidePanelApplication>({
  handlePanelRef,
  application,
  isLoading,
  onClose,
  onToggle,
  isVisible,
  rowCount,
  currentIndex,
  previousCandidate,
  nextCandidate,
  selectCandidate,
  applicationId,
  activeSidePanelTab,
  setActiveSidePanelTab,
  canDownloadCandidateData,
  externalDisableClickOutside,
  externalModalsOpen,
  renderHeader,
  children,
}: Props<T>) => {
  const [extendedResumePreview, setExtendedResumePreview] =
    React.useState<Document | null>(null);
  const [linkedinPreview, setLinkedinPreview] = React.useState<string | null>(
    null
  );
  const [modalDocument, setModalDocument] = React.useState<Document | null>(
    null
  );

  const viewportWidth = useViewportWidth(MIN_EXTENDED_VIEW_WIDTH);
  const { calibrationTerms } = useUserContext();
  const panelContentRef = React.useRef<HTMLDivElement>(null);
  usePreventScroll(panelContentRef);

  useDefaultSidePanelTab({
    applicationId,
    application: application as unknown as Person,
    isLoading,
    isPanelVisible: isVisible,
    activeSidePanelTab,
    setActiveSidePanelTab,
  });

  const handleClosePanel = useCallback(() => {
    setExtendedResumePreview(null);
    setLinkedinPreview(null);
    onClose();
  }, [onClose]);

  const handleToggle = useCallback(() => {
    setExtendedResumePreview(null);
    setLinkedinPreview(null);
    onToggle();
  }, [onToggle]);

  const allowExtendedPreview = viewportWidth >= MIN_EXTENDED_VIEW_WIDTH;

  useSyncResumePreviewOnCandidateChange({
    applicationId: application?.id,
    applicationResumeDocument: application?.resume?.document,
    extendedResumePreview,
    linkedinPreview,
    modalDocument,
    setExtendedResumePreview,
    setLinkedinPreview,
    setModalDocument,
    allowExtendedPreview,
    isVisible: isVisible ?? false,
  });

  useEscapeKeyToClose({
    modalsOpen: externalModalsOpen ?? false,
    extendedResumePreview,
    modalDocument,
    isVisible,
    handleClosePanel,
    setExtendedResumePreview,
    setModalDocument,
  });

  useExtendedPreviewVsModal({
    allowExtendedPreview,
    extendedResumePreview,
    modalDocument,
    setExtendedResumePreview,
    setModalDocument,
  });

  const hasExtendedPreview = extendedResumePreview || linkedinPreview;
  const hasBothPreviews = extendedResumePreview && linkedinPreview;
  const previewCount = hasBothPreviews ? 2 : 1;
  const computedPanelWidth =
    allowExtendedPreview && hasExtendedPreview
      ? `min(${
          PANEL_BASE_WIDTH + EXTENDED_PREVIEW_MAX_WIDTH * previewCount
        }px, calc(100vw - ${EXTENDED_GUTTER}px))`
      : `${PANEL_BASE_WIDTH}px`;

  const disableClickOutside =
    !!modalDocument || (externalDisableClickOutside ?? false);

  const renderProps: BaseSidePanelRenderProps<T> | null = application
    ? {
        application,
        isLoading,
        setExtendedResumePreview,
        setLinkedinPreview,
        setModalDocument,
        extendedResumePreview,
        modalDocument,
        allowExtendedPreview,
      }
    : null;

  return (
    <SidePanel
      ref={handlePanelRef}
      id="side_panel"
      onClose={handleClosePanel}
      isVisible={isVisible}
      disableClickOutside={disableClickOutside}
      width={computedPanelWidth}
      onOutsideClick={() => {
        setExtendedResumePreview(null);
        setLinkedinPreview(null);
        setModalDocument(null);
      }}
    >
      <PanelLayout>
        {allowExtendedPreview && extendedResumePreview && application && (
          <ResumePreviewContainer>
            <ResumePreviewBody>
              <DocumentHeader
                document={extendedResumePreview}
                onClose={() => setExtendedResumePreview(null)}
                candidate={application as unknown as Person}
                canDownloadCandidateData={canDownloadCandidateData}
                showCandidateActions={false}
              />
              <DocumentContent
                document={extendedResumePreview}
                hasCalibrationTerms={Boolean(calibrationTerms?.length)}
              />
            </ResumePreviewBody>
          </ResumePreviewContainer>
        )}
        {allowExtendedPreview && linkedinPreview && (
          <ResumePreviewContainer>
            <ResumePreviewBody>
              <LinkedinHeader onClose={() => setLinkedinPreview(null)} />
              <BorderedFrame>
                <LinkedinContent iframePath={linkedinPreview} />
              </BorderedFrame>
            </ResumePreviewBody>
          </ResumePreviewContainer>
        )}

        <RightPanelColumn spacing={0} justifyContent="space-between">
          <PanelContent ref={panelContentRef}>
            {isLoading ? (
              <Stack>
                <LoadingSpinner size="medium" />
              </Stack>
            ) : renderProps ? (
              <FullHeightStack spacing={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{
                    width: '100%',
                  }}
                >
                  {renderHeader(renderProps)}
                  <SidePanelIconButton>
                    <IconButton
                      icon={CloseIcon}
                      size="md"
                      onClick={handleToggle}
                      label={t('actions.close_panel')}
                      flat
                    />
                  </SidePanelIconButton>
                </Stack>
                {children(renderProps)}
              </FullHeightStack>
            ) : (
              <Stack spacing={1}>
                <CandidateName>{t('panel.error.title')}</CandidateName>
                <Body>{t('panel.error.message')}</Body>
              </Stack>
            )}
          </PanelContent>

          <Footer>
            <RowStepper
              currentIndex={currentIndex}
              rowCount={rowCount}
              previousCandidate={previousCandidate}
              nextCandidate={nextCandidate}
              selectCandidate={selectCandidate}
            />
          </Footer>
        </RightPanelColumn>
      </PanelLayout>
    </SidePanel>
  );
};

export default BaseSidePanel;
